/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { atom } from "jotai";
import { streetApi } from "src/config/axios";
import { createDraft, finishDraft } from "immer";
import getCurrentUserId from "src/utils/currentUserId";
import { format, nextSunday } from "date-fns";
import type { DateRange } from "react-day-picker";
import type {
    CalenderPageEventObject,
    EventType,
    TopPageSegmentedControlKey,
} from "./event.type";
import {
    getArtistKeywordUrl,
    getEventDateUrl,
    getEventTypesUrl,
} from "./event.utile";

// --------------------------------------
// イベントタイプ機能のためのatom
// --------------------------------------

const eventType = atom<EventType>({
    streetLive: true,
    event: true,
    otherEvent: true,
});

export const eventTypeAtom = atom(
    (get) => {
        return get(eventType);
    },
    (get, set, eventTypeKey: string) => {
        const preData = get(eventType);
        const newData = createDraft(preData);
        newData[eventTypeKey] = !newData[eventTypeKey];

        // すべてのeventTypeがfalseの場合は、クリックしたeventTypeのみtrueにする
        const isAllFalse = Object.values(newData).every((value) => {
            return value === false;
        });

        if (isAllFalse) {
            newData[eventTypeKey] = true;
        }

        const nextState = finishDraft(newData);

        set(eventType, nextState);
    }
);

// --------------------------------------
// SegmentedControl機能のためのatom
// --------------------------------------

const topPageSegmentedControlKey = atom<TopPageSegmentedControlKey>("all");

export const topPageSegmentedControlKeyAtom = atom(
    (get) => {
        return get(topPageSegmentedControlKey);
    },
    (_, set, newKey: TopPageSegmentedControlKey) => {
        set(topPageSegmentedControlKey, newKey);
    }
);

// --------------------------------------
// キーワード検索機能のためのatom
// --------------------------------------

const searchKeyword = atom<string>("");

export const searchKeywordAtom = atom(
    (get) => {
        return get(searchKeyword);
    },
    (_, set, newKeyword: string) => {
        set(searchKeyword, newKeyword);
    }
);

// --------------------------------------
// トップページのイベント一覧を取得するためのatom
// --------------------------------------

const currentPage = atom<number>(1);

const topPageEventListObject = atom<{
    [key: string]: any[];
}>({});

const isLoadedLastPage = atom<boolean>(false);

const isFirstFetch = atom<boolean>(false);

export const currentPageAtom = atom(
    (get) => {
        return get(currentPage);
    },
    (_, set, pageCount: number) => {
        set(currentPage, pageCount);
    }
);

export const isLoadedLastPageAtom = atom(
    (get) => {
        return get(isLoadedLastPage);
    },
    (_, set, isLastPage: boolean) => {
        set(isLoadedLastPage, isLastPage);
    }
);

export const isFirstFetchAtom = atom(
    (get) => {
        return get(isFirstFetch);
    },
    (_, set, isFirstFetchBool: boolean) => {
        set(isFirstFetch, isFirstFetchBool);
    }
);

const isLastPage = (last_page: number, current_page: number) => {
    if (last_page === current_page) {
        return true;
    }
    return false;
};

const objectFormat = (newData: any, preData: any) => {
    const draft = createDraft(preData);

    Object.entries(newData).forEach(([date, events]) => {
        // すでに日付が存在している場合はそのまま追加、なければ新しいdateをキーにして新規作成
        if (draft[date]) {
            const eventsObject: any = events;
            // dateにpushするとき、すでに存在しているeventsのidと新しいeventsのidが重複する場合は追加しない
            eventsObject.forEach((newEvent: any) => {
                const isExist = draft[date].some((event: any) => {
                    return event.id === newEvent.id;
                });
                if (!isExist) {
                    draft[date].push(newEvent);
                }
            });
        } else {
            const eventsObject: any = events;
            draft[date] = [...eventsObject];
        }
    });

    const nextState = finishDraft(draft);
    return nextState;
};

export const topPageEventListObjectAtom = atom(
    (get) => {
        return get(topPageEventListObject);
    },
    async (get, set, isReset: boolean) => {
        const currentUserId = getCurrentUserId() ?? "";
        const pageCount = isReset ? 1 : get(currentPage);
        const eventType = get(eventTypeAtom);
        const searchKeyword = get(searchKeywordAtom);
        const topPageSegmentedControlKey = get(topPageSegmentedControlKeyAtom);
        const artistKeyword = get(artistSearchKeywordAtom);
        const place = get(placeSearchKeywordAtom);
        const date = get(searchDateAtom);
        const includeCompletedEventOption = get(
            isFilteredOfCompletedEventsAtom
        );

        const getKeywordUrl = (keyword: string) => {
            if (keyword !== "") {
                return `&keyword=${keyword}`;
            }
            return "";
        };

        const getEventTabUrl = (segmentKey: string) => {
            switch (segmentKey) {
                case "all":
                    return "";
                case "todays":
                    return "&tab=todays_events";
                case "following":
                    return "&tab=followed_events";
                default:
                    return "";
            }
        };

        const getPlaceUrl = (place: string) => {
            if (place !== "") {
                return `&place=${place}`;
            }
            return "";
        };

        const getIncludeCompletedEventOptionUrl = (option: boolean) => {
            if (option) {
                return "&option=true";
            }
            return "";
        };

        const url = `/events?page=${pageCount}${getKeywordUrl(searchKeyword)}${getEventTabUrl(
            topPageSegmentedControlKey
        )}${getPlaceUrl(place)}${getEventDateUrl(date)}${getIncludeCompletedEventOptionUrl(
            includeCompletedEventOption
        )}`;

        await streetApi.get(url).then((response) => {
            const preData = isReset ? {} : get(topPageEventListObject);
            const newData = response.data.events;

            const paginationOb = response.data.pagination;
            const isLast = isLastPage(paginationOb.last_page, pageCount);

            const fetchEventObject = objectFormat(newData, preData);

            set(topPageEventListObject, fetchEventObject);
            set(isFirstFetchAtom, true);
            set(currentPage, pageCount + 1);
            set(isLoadedLastPageAtom, isLast);
        });
    }
);

// イベントのidを受け取って、そのイベントをtopPageEventListObjectから削除する
export const deleteEventAtom = atom(null, (get, set, eventId: number) => {
    const preData = get(topPageEventListObject);
    const nextState = createDraft(preData);
    Object.entries(nextState).forEach(([date, events]) => {
        nextState[date] = events.filter((event: any) => {
            return event.id !== eventId;
        });
    });
    set(topPageEventListObject, finishDraft(nextState));
});

// --------------------------------------
// いいね機能のためのatom
// --------------------------------------

const operatedLikesList = atom<{
    [key: number]: {
        isLiked: boolean;
        likesCount: number;
    };
}>({});

export const operatedLikesListAtom = atom(
    (get) => {
        return get(operatedLikesList);
    },
    (get, set, eventId: number, isLikes: boolean, likeCount: number) => {
        const preData = get(operatedLikesListAtom);
        const nextState = createDraft(preData);
        nextState[eventId] = {
            isLiked: isLikes,
            likesCount: likeCount,
        };
        set(operatedLikesList, finishDraft(nextState));
    }
);

// --------------------------------------
// ブックマーク機能のためのatom
// --------------------------------------

const operatedBookMarksList = atom<Record<number, boolean>>({});

export const operatedBookMarksListAtom = atom(
    (get) => {
        return get(operatedBookMarksList);
    },
    (get, set, eventId: number, isBookMarks: boolean) => {
        const preData = get(operatedBookMarksList);
        const nextState = createDraft(preData);
        nextState[eventId] = isBookMarks;
        set(operatedBookMarksList, finishDraft(nextState));
    }
);

// --------------------------------------
// カレンダー機能のためのatom
// --------------------------------------

const calenderPageEventObject = atom<CalenderPageEventObject>({});

export const calenderPageEventObjectAtom = atom(
    (get) => {
        return get(calenderPageEventObject);
    },
    (_, set, newData: CalenderPageEventObject) => {
        set(calenderPageEventObject, newData);
    }
);

// --------------------------------------
// イベントカードレイアウト変更機能のためのatom
// --------------------------------------

const eventCardLayout = atom<string>("small");

export const eventCardLayoutAtom = atom(
    (get) => {
        return get(eventCardLayout);
    },
    (get, set) => {
        const layout = get(eventCardLayout);

        if (layout === "large") {
            set(eventCardLayout, "small");
        } else {
            set(eventCardLayout, "large");
        }
    }
);

// --------------------------------------
// イベントタブタブ保存機能のためのatom
// --------------------------------------

const segmentedControlCurrentIndex = atom(0);

export const segmentedControlCurrentIndexAtom = atom(
    (get) => {
        return get(segmentedControlCurrentIndex);
    },
    (_, set, newIndex: number) => {
        set(segmentedControlCurrentIndex, newIndex);
    }
);

// --------------------------------------
// 日時指定検索のためのatom
// --------------------------------------

const searchDate = atom({
    startDate: "",
    endDate: "",
});

export const searchDateAtom = atom(
    (get) => {
        return get(searchDate);
    },
    (_, set, newDate: { startDate: string; endDate: string }) => {
        set(searchDate, newDate);
    }
);

const displaySearchDate = atom<null | {
    startDate: string;
    endDate: string;
}>(null);

export const displaySearchDateAtom = atom(
    (get) => {
        return get(displaySearchDate);
    },
    (_, set, newDate: { startDate: Date; endDate: Date }) => {
        const formatDateStringForDisplaySearchDate = (date: Date) => {
            return format(date, "yyyy/MM/dd");
        };

        const formatDateStringForSearchDate = (date: Date) => {
            return format(date, "yyyy-MM-dd");
        };

        const newDisplaySearchDate = {
            startDate: formatDateStringForDisplaySearchDate(newDate.startDate),
            endDate: formatDateStringForDisplaySearchDate(newDate.endDate),
        };

        const newSearchDate = {
            startDate: formatDateStringForSearchDate(newDate.startDate),
            endDate: formatDateStringForSearchDate(newDate.endDate),
        };

        set(displaySearchDate, newDisplaySearchDate);
        set(searchDate, newSearchDate);
        set(searchDateActiveIndex, 3);
    }
);

const searchDateActiveIndex = atom<number | null>(null);

export const searchDateActiveIndexAtom = atom(
    (get) => {
        return get(searchDateActiveIndex);
    },
    (_, set, newIndex: number | null, variant: string) => {
        set(searchDateActiveIndex, newIndex);

        switch (variant) {
            case "today": {
                set(searchDate, {
                    startDate: format(new Date(), "yyyy-MM-dd"),
                    endDate: format(new Date(), "yyyy-MM-dd"),
                });
                break;
            }
            case "tomorrow": {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);

                set(searchDate, {
                    startDate: format(tomorrow, "yyyy-MM-dd"),
                    endDate: format(tomorrow, "yyyy-MM-dd"),
                });
                break;
            }
            case "thisWeek": {
                const today = new Date();

                const sunday = nextSunday(today);

                set(searchDate, {
                    startDate: format(today, "yyyy-MM-dd"),
                    endDate: format(sunday, "yyyy-MM-dd"),
                });
                break;
            }
            case "reset": {
                set(searchDate, {
                    startDate: "",
                    endDate: "",
                });
                break;
            }
            default:
                break;
        }
    }
);

const datePickerSelectedRange = atom<DateRange | undefined>(undefined);

export const datePickerSelectedRangeAtom = atom(
    (get) => {
        return get(datePickerSelectedRange);
    },
    (_, set, newRange: DateRange | undefined) => {
        set(datePickerSelectedRange, newRange);
    }
);

// --------------------------------------
// アーティスト検索のためのatom
// --------------------------------------

const artistSearchKeyword = atom<string>("");

export const artistSearchKeywordAtom = atom(
    (get) => {
        return get(artistSearchKeyword);
    },
    (_, set, newKeyword: string) => {
        set(artistSearchKeyword, newKeyword);
    }
);

// --------------------------------------
// 場所検索のためのatom
// --------------------------------------

const placeSearchKeyword = atom<string>("");

export const placeSearchKeywordAtom = atom(
    (get) => {
        return get(placeSearchKeyword);
    },
    (_, set, newKeyword: string) => {
        set(placeSearchKeyword, newKeyword);
    }
);

// --------------------------------------
// 料金検索のためのatom
//  --------------------------------------

const eventPriceSelectorActiveIndex = atom<number>(0);

export const eventPriceSelectorActiveIndexAtom = atom(
    (get) => {
        return get(eventPriceSelectorActiveIndex);
    },
    (_, set, newIndex: number) => {
        set(eventPriceSelectorActiveIndex, newIndex);
    }
);

// --------------------------------------
// 終了したイベントを含ませるかのatom
//  --------------------------------------

const isFilteredOfCompletedEvents = atom<boolean>(false);

export const isFilteredOfCompletedEventsAtom = atom(
    (get) => {
        return get(isFilteredOfCompletedEvents);
    },
    (get, set) => {
        set(isFilteredOfCompletedEvents, !get(isFilteredOfCompletedEvents));
    }
);

// フィルターモーダルの元状態を保持するためのatom

const filterModalState = atom<{
    artistKeyword: string;
    keyword: string;
    place: string;
    date: {
        startDate: string;
        endDate: string;
    };
    dateActiveIndex: number | null;
    price: number;
}>({
    artistKeyword: "",
    keyword: "",
    place: "",
    date: {
        startDate: "",
        endDate: "",
    },
    dateActiveIndex: null,
    price: 0,
});

export const filterModalStateAtom = atom(
    (get) => {
        return get(filterModalState);
    },
    (get, set, define: boolean) => {
        const originState = get(filterModalState);

        if (define) {
            const artistKeyword = get(artistSearchKeywordAtom);
            const keyword = get(searchKeywordAtom);
            const place = get(placeSearchKeywordAtom);
            const date = get(searchDateAtom);
            const dateActiveIndex = get(searchDateActiveIndexAtom);
            const price = get(eventPriceSelectorActiveIndexAtom);

            const nextState = {
                artistKeyword,
                keyword,
                place,
                date,
                dateActiveIndex,
                price,
            };

            set(filterModalState, nextState);
        } else {
            set(artistSearchKeywordAtom, originState.artistKeyword);
            set(searchKeywordAtom, originState.keyword);
            set(placeSearchKeywordAtom, originState.place);
            set(searchDateAtom, originState.date);
            set(searchDateActiveIndexAtom, originState.dateActiveIndex, "");
            set(eventPriceSelectorActiveIndexAtom, originState.price);
        }
    }
);

// --------------------------------------
// アーティスト紹介
// --------------------------------------

const alreadyShowedRecommendedArtistDialog = atom<boolean>(false);

export const alreadyShowedRecommendedArtistDialogAtom = atom(
    (get) => {
        return get(alreadyShowedRecommendedArtistDialog);
    },
    (_, set) => {
        set(alreadyShowedRecommendedArtistDialog, true);
    }
);
